/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { IExample } from "@chatcaptain/types/chatbot";
import {
  collection,
  getDocs,
  getFirestore,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { fetchCloudRun } from "../Helper";

/**
 * Gets all examples by intent id and language
 * @param chatbotId id of the chatbot
 * @param intentId id of the intent
 * @param language language code
 * @returns IExample array
 */
export const getExamples = async (
  chatbotId: string,
  intentId: string,
  language: string
): Promise<IExample[]> => {
  const examples: IExample[] = [];
  const docs = await getDocs(
    query(
      collection(getFirestore(), "dialogs/" + chatbotId + "/examples"),
      where("intent_id", "==", intentId),
      where("language", "==", language),
      orderBy("created", "desc")
    )
  );
  docs.forEach((doc) => {
    examples.push({
      id: doc.id,
      ...(doc.data() as IExample),
    });
  });
  return examples;
};

/**
 * Creates a new example
 * @param chatbotId id of the current chatbot
 * @param text
 * @param language
 * @returns the new id of the created example
 */
export const createExample = async (
  chatbotId: string,
  text: string,
  entities: [],
  language: string,
  intentId: string,
  translate?: boolean
): Promise<string> => {
  const response = await fetchCloudRun(
    "https://app.chatcaptain.com/api/bot/chatbots/" + chatbotId + "/examples",
    "POST",
    {},
    {
      text,
      language,
      entities,
      intent_id: intentId,
      translate: translate || false,
    }
  );
  return response.id;
};

/**
 * Deletes an example
 * @param chatbotId id of the current chatbot
 * @param exampleId id of the example
 */
export const deleteExample = async (chatbotId: string, exampleId: string) => {
  await fetchCloudRun(
    "https://app.chatcaptain.com/api/bot/chatbots/" +
      chatbotId +
      "/examples/" +
      exampleId,
    "DELETE"
  );
};
